import { AdElementText, DateTimeFormat } from '@adflow/types';
import { FormLabel, Radio, RadioGroup, Stack, Switch } from '@chakra-ui/react';
import { FC, useCallback } from 'react';
import { useStore } from '../../../../hooks/useStore';

type Props = {
  textElement: AdElementText;
};

export const isTimePresent = (textElement: AdElementText) => {
  if (!textElement.sources?.length) return false;
  return textElement.sources.some((s) => textElement.data.content.includes(`${s.sourceId}:TIME}`));
};

const TimeFormatControls: FC<Props> = ({ textElement }) => {
  const s = useStore((state) => ({
    displayOptions: state.displayOptions,
    setElementDateDisplayOptions: state.setElementDateDisplayOptions,
  }));

  const dateDisplayOptions: DateTimeFormat | null | undefined =
    textElement.data.dateDisplayOptions || s.displayOptions.dateTimeFormat;

  const handleUpdateTimeFormat = useCallback(
    (value: string) => {
      let timeFormat = dateDisplayOptions?.timeFormat;
      if (timeFormat == null) {
        timeFormat = 'hour24';
      }

      if (value !== 'hour12' && value !== 'hour24') return;
      timeFormat = value;
      s.setElementDateDisplayOptions(textElement.id, {
        ...dateDisplayOptions,
        timeFormat: timeFormat,
      });
    },
    [s, dateDisplayOptions, textElement.id],
  );

  const handleUpdateShowTimezone = useCallback(
    (enabled: boolean) => {
      s.setElementDateDisplayOptions(textElement.id, {
        ...dateDisplayOptions,
        showTimezone: enabled,
      });
    },
    [s, dateDisplayOptions, textElement.id],
  );

  return (
    <>
      <FormLabel htmlFor="time-format" mb={'0'} fontWeight={'semibold'}>
        Time Format
      </FormLabel>
      <Stack direction="row" mb={2} justifyContent={'space-between'}>
        <RadioGroup
          value={dateDisplayOptions?.timeFormat || 'hour24'}
          id="time-format"
          onChange={handleUpdateTimeFormat}
        >
          <Stack direction="row">
            <Radio value="hour12">12-hour</Radio>
            <Radio value="hour24">24-hour</Radio>
          </Stack>
        </RadioGroup>
        <Stack direction="row" alignItems="center">
          <FormLabel fontWeight={'normal'} mb={'0'}>
            Include timezone?
          </FormLabel>
          <Switch
            isChecked={dateDisplayOptions?.showTimezone || false}
            onChange={(evt) => handleUpdateShowTimezone(evt.target.checked)}
          />
        </Stack>
      </Stack>
    </>
  );
};

TimeFormatControls.displayName = 'TimeFormatControls';

export default TimeFormatControls;
